import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CircleCheck, AlertCircle, X } from 'lucide-react';

// ───────────────────────── Toasts
const ToastContext = createContext(() => {});

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const id = useRef(0);

  const toast = useCallback((message, type = 'succes') => {
    const tid = ++id.current;
    setToasts(t => [...t.slice(-2), { id: tid, message, type }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== tid)), 3200);
  }, []);

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {toasts.map(t => (
          <div key={t.id} className={`toast toast-${t.type}`}>
            {t.type === 'erreur' ? <AlertCircle size={18} /> : <CircleCheck size={18} />}
            <span>{t.message}</span>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => useContext(ToastContext);

// ───────────────────────── Media query
export const useMediaQuery = (query) => {
  const get = () => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(query).matches : false);
  const [match, setMatch] = useState(get);
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia(query);
    const onChange = () => setMatch(mq.matches);
    onChange();
    mq.addEventListener ? mq.addEventListener('change', onChange) : mq.addListener(onChange);
    return () => (mq.removeEventListener ? mq.removeEventListener('change', onChange) : mq.removeListener(onChange));
  }, [query]);
  return match;
};

export const useDesktop = () => useMediaQuery('(min-width: 960px)');

// ───────────────────────── Fermeture par Échap (overlay du dessus uniquement) + blocage du scroll
const pile = [];

const useOverlay = (open, onClose) => {
  const fermer = useRef(onClose);
  fermer.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    const jeton = {};
    pile.push(jeton);
    const onKey = (e) => {
      if (e.key === 'Escape' && pile[pile.length - 1] === jeton) fermer.current();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      pile.splice(pile.indexOf(jeton), 1);
      if (pile.length === 0) document.body.style.overflow = '';
    };
  }, [open]);
};

// ───────────────────────── Modal centrée
export const Modal = ({ open, onClose, titre, children, etroite }) => {
  useOverlay(open, onClose);
  if (!open) return null;
  return createPortal(
    <div className="overlay" onClick={onClose}>
      <div className={`modal ${etroite ? 'modal-etroite' : ''}`} role="dialog" aria-modal="true" aria-label={titre}
        onClick={e => e.stopPropagation()}>
        {titre && (
          <div className="modal-tete">
            <h2>{titre}</h2>
            <button className="btn-icone" onClick={onClose} aria-label="Fermer"><X size={20} /></button>
          </div>
        )}
        {children}
      </div>
    </div>,
    document.body
  );
};

// ───────────────────────── Bottom sheet (mobile)
export const Sheet = ({ open, onClose, children, label }) => {
  useOverlay(open, onClose);
  if (!open) return null;
  return createPortal(
    <div className="overlay overlay-sheet" onClick={onClose}>
      <div className="sheet" role="dialog" aria-modal="true" aria-label={label} onClick={e => e.stopPropagation()}>
        <button className="sheet-poignee" onClick={onClose} aria-label="Fermer"><span /></button>
        {children}
      </div>
    </div>,
    document.body
  );
};

// ───────────────────────── Confirmation (remplace window.confirm)
export const ConfirmModal = ({ open, titre, code, message, labelConfirmer = 'Supprimer', danger = true, onConfirm, onClose }) => {
  const [enCours, setEnCours] = useState(false);
  const confirmer = async () => {
    setEnCours(true);
    try { await onConfirm(); } finally { setEnCours(false); }
  };
  return (
    <Modal open={open} onClose={enCours ? () => {} : onClose} etroite>
      <div className="confirm">
        <h2>{titre}</h2>
        {code && <div className="cau cau-moyen">{code}</div>}
        {message && <p className="texte-secondaire">{message}</p>}
        <div className="confirm-actions">
          <button className="btn btn-secondaire" onClick={onClose} disabled={enCours}>Annuler</button>
          <button className={`btn ${danger ? 'btn-danger' : 'btn-primaire'}`} onClick={confirmer} disabled={enCours}>
            {enCours ? '…' : labelConfirmer}
          </button>
        </div>
      </div>
    </Modal>
  );
};

// ───────────────────────── Skeletons
export const SkeletonCarte = () => (
  <div className="carte-lieu skeleton-carte" aria-hidden="true">
    <div className="sk sk-ligne" style={{ width: '30%' }} />
    <div className="sk sk-titre" style={{ width: '55%' }} />
    <div className="sk sk-ligne" style={{ width: '75%' }} />
    <div className="sk sk-ligne" style={{ width: '45%' }} />
    <div className="sk sk-bouton" />
  </div>
);

export const SkeletonListe = ({ n = 3 }) => (
  <div className="liste-lieux" aria-busy="true" aria-label="Chargement">
    {Array.from({ length: n }, (_, i) => <SkeletonCarte key={i} />)}
  </div>
);

export const SkeletonPage = () => (
  <div className="page" aria-busy="true">
    <div className="sk sk-titre" style={{ width: '40%', marginBottom: 16 }} />
    <SkeletonListe n={2} />
  </div>
);
